import { useState } from 'react';
import type { AppSession, MeView, PatientListEntry, PatientStatus, PracticeView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtDate } from '../../lib/format';
import { patientHash } from '../../lib/routes';
import { Icon } from '../../components/Mark';
import { Avatar, Card, Empty } from '../../components/Bits';

const WEEK = 7 * 86_400_000;

export const STATUS: Record<PatientStatus, { label: string; tone: string; help: string }> = {
  pending: { label: 'Pending', tone: 'badge-soft', help: 'On your list, not invited yet' },
  eligible: { label: 'Eligible', tone: 'badge-gold', help: 'Invited: can create an account with this email address' },
  enrolled: { label: 'Enrolled', tone: 'badge-green', help: 'Has created their account' },
};

const ORDER: Record<PatientStatus, number> = { pending: 0, eligible: 1, enrolled: 2 };

export function StatusBadge({ status }: { status: PatientStatus }) {
  const s = STATUS[status];
  return (
    <span className={`badge ${s.tone}`} title={s.help}>
      {s.label}
    </span>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A link that could not be emailed, shown so the manager can hand it over another way. */
export function LinkBox({ link, say }: { link: string; say: (s: string) => void }) {
  return (
    <div className="link-box">
      <code className="mono">{link}</code>
      <button type="button" className="secondary small" onClick={() => void copyText(link).then((ok) => say(ok ? 'Invitation link copied.' : 'Could not copy; select the link and copy it.'))}>
        Copy
      </button>
    </div>
  );
}

/** The clinical manager's front page: everyone on the practice's list, where they are in sign-up, and when they were last active. */
export function ManagerPatients({ session, practice, onChanged, say }: { session: AppSession; me: MeView; practice: PracticeView; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const [filter, setFilter] = useState<'all' | PatientStatus>('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [shared, setShared] = useState<{ name: string; link: string; reason: string } | null>(null);

  const list = practice.patientList;
  const count = (s: PatientStatus) => list.filter((p) => p.status === s).length;
  const now = Date.now();
  const active = list.filter((p) => p.status === 'enrolled' && p.lastActiveAt !== null && now - p.lastActiveAt < WEEK).length;
  const needle = q.trim().toLowerCase();
  const shown = list
    .filter((p) => filter === 'all' || p.status === filter)
    .filter((p) => !needle || p.name.toLowerCase().includes(needle) || (p.email ?? '').includes(needle))
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.name.localeCompare(b.name));

  const invite = async (e: PatientListEntry) => {
    setBusy(e.id);
    try {
      const r = await api.invitePatient(e.id, session.token);
      if (r.email.sent) {
        say(`Invitation emailed to ${e.email}. ${e.name} is now eligible.`);
        setShared(null);
      } else {
        say(`${e.name} is now eligible. The invitation was not emailed, so share the link below.`);
        setShared({ name: e.name, link: r.link, reason: r.email.reason ?? '' });
      }
      await onChanged();
    } catch (err) {
      say(err instanceof ApiError ? err.message : 'Could not send the invitation.');
    } finally {
      setBusy(null);
    }
  };

  const inviteAll = async () => {
    setBusy('all');
    try {
      const r = await api.invitePending(session.token);
      const emailed = r.invited.filter((i) => i.email.sent).length;
      const byLink = r.invited.length - emailed;
      say(`${r.invited.length} invited: ${emailed} emailed${byLink > 0 ? `, ${byLink} to share by link` : ''}${r.failed.length > 0 ? `, ${r.failed.length} failed` : ''}.`);
      await onChanged();
    } catch (err) {
      say(err instanceof ApiError ? err.message : 'Could not send the invitations.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (e: PatientListEntry) => {
    if (!confirm(`Take ${e.name} off the patient list? Their invitation link stops working.`)) return;
    setBusy(e.id);
    try {
      await api.removeListEntry(e.id, session.token);
      say(`${e.name} removed from the list.`);
      await onChanged();
    } catch (err) {
      say(err instanceof ApiError ? err.message : 'Could not remove them.');
    } finally {
      setBusy(null);
    }
  };

  const pending = count('pending');

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>Patients</h1>
          <span className="hint">
            {practice.name} · {list.length} on your list
          </span>
        </div>
        <div className="actions">
          {pending > 0 ? (
            <button type="button" className="secondary" disabled={busy !== null} onClick={() => void inviteAll()}>
              {busy === 'all' ? 'Inviting…' : `Invite all pending (${pending})`}
            </button>
          ) : null}
          <a className="button primary" href="#/practice/add">
            <Icon name="plus" size={16} /> Add patients
          </a>
        </div>
      </header>

      <div className="stat-grid">
        <Card className="stat-card">
          <span className="hint">On the list</span>
          <strong>{list.length}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Pending</span>
          <strong>{pending}</strong>
          <span className="hint small">not invited yet</span>
        </Card>
        <Card className="stat-card">
          <span className="hint">Eligible</span>
          <strong>{count('eligible')}</strong>
          <span className="hint small">invited, no account yet</span>
        </Card>
        <Card className="stat-card">
          <span className="hint">Enrolled</span>
          <strong>{count('enrolled')}</strong>
          <span className="hint small">account created</span>
        </Card>
        <Card className="stat-card">
          <span className="hint">Active this week</span>
          <strong>
            {active}
            <small> of {count('enrolled')}</small>
          </strong>
        </Card>
      </div>

      {shared ? (
        <Card className="card-soft">
          <div className="card-head">
            <h2>Share {shared.name}'s invitation</h2>
            <button type="button" className="quiet small" onClick={() => setShared(null)}>
              Done
            </button>
          </div>
          <p className="hint">{shared.reason ? `${shared.reason.replace(/^not emailed: /, 'Not emailed: ')}. ` : ''}Send this link to them yourself. It works once, only with the email address on your list.</p>
          <LinkBox link={shared.link} say={say} />
        </Card>
      ) : null}

      <Card className="table-card">
        <div className="toolbar">
          <div className="segmented" role="tablist" aria-label="Filter by status">
            {(['all', 'pending', 'eligible', 'enrolled'] as const).map((f) => (
              <button key={f} type="button" role="tab" aria-selected={filter === f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                {f === 'all' ? `All (${list.length})` : `${STATUS[f].label} (${count(f)})`}
              </button>
            ))}
          </div>
          <input type="search" value={q} placeholder="Search name or email" aria-label="Search patients" onChange={(e) => setQ(e.target.value)} />
        </div>
        {list.length === 0 ? (
          <Empty title="Nobody on the list yet">Add your patients by name and email. Invite them, and each one can create an account with the address you entered.</Empty>
        ) : shown.length === 0 ? (
          <Empty title="No one matches">Try another filter or search.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Status</th>
                  <th>Doctor</th>
                  <th>Invited</th>
                  <th>Enrolled</th>
                  <th>Last active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="patient-cell">
                        <Avatar name={p.name} size="sm" />
                        <span className="row-main">
                          <strong>{p.status === 'enrolled' && p.patientId ? <a href={patientHash(p.patientId)}>{p.name}</a> : p.name}</strong>
                          <span className="hint">{p.email ?? 'no email on file'}</span>
                        </span>
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td>{p.doctorName}</td>
                    <td>
                      {p.invitedAt ? (
                        <span className="row-main">
                          <span>
                            {fmtAgo(p.invitedAt)}
                            {p.inviteCount > 1 ? ` · ${p.inviteCount} times` : ''}
                          </span>
                          <span className={`hint small ${p.emailStatus === 'sent' ? '' : 'warn'}`} title={p.emailStatus ?? ''}>
                            {p.emailStatus === 'sent' ? 'emailed' : 'not emailed: share the link'}
                          </span>
                        </span>
                      ) : (
                        <span className="hint">—</span>
                      )}
                    </td>
                    <td>{p.enrolledAt ? fmtDate(p.enrolledAt) : <span className="hint">—</span>}</td>
                    <td>{p.status !== 'enrolled' ? <span className="hint">—</span> : p.lastActiveAt ? <span className={now - p.lastActiveAt > WEEK ? 'warn' : ''}>{fmtAgo(p.lastActiveAt)}</span> : <span className="hint">never</span>}</td>
                    <td>
                      <span className="actions">
                        {p.status === 'pending' ? (
                          <button type="button" className="primary small" disabled={busy !== null || !p.email} onClick={() => void invite(p)}>
                            {busy === p.id ? 'Sending…' : 'Send invite'}
                          </button>
                        ) : null}
                        {p.status === 'eligible' ? (
                          <>
                            <button type="button" className="secondary small" disabled={busy !== null} onClick={() => void invite(p)}>
                              {busy === p.id ? 'Sending…' : 'Resend'}
                            </button>
                            {p.inviteLink ? (
                              <button type="button" className="quiet small" onClick={() => void copyText(p.inviteLink as string).then((ok) => say(ok ? `Invitation link for ${p.name} copied.` : 'Could not copy the link.'))}>
                                Copy link
                              </button>
                            ) : null}
                          </>
                        ) : null}
                        {p.status !== 'enrolled' ? (
                          <button type="button" className="quiet small" disabled={busy !== null} onClick={() => void remove(p)}>
                            Remove
                          </button>
                        ) : p.patientId ? (
                          <a className="quiet small" href={patientHash(p.patientId)}>
                            View
                          </a>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <p className="status-legend">
        {(['pending', 'eligible', 'enrolled'] as const).map((s) => (
          <span key={s}>
            <StatusBadge status={s} /> {STATUS[s].help}
          </span>
        ))}
      </p>
    </>
  );
}
